import React, { useState, useRef, useEffect } from 'react';
import { IMessage } from '../../services/messaging/IMessagingProvider';
import { MessageMedia } from './MessageMedia';

const QUICK_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🙏'];

// Matches a message whose content is made up of only 1-3 emoji (no text) —
// such messages render "sticker-style": much larger, no bubble background,
// like WhatsApp/iMessage/Telegram do for emoji-only messages.
const EMOJI_ONLY_REGEX = /^(?:\p{Extended_Pictographic}|\p{Emoji_Presentation}|[‍️⃣]|[\u{1F1E6}-\u{1F1FF}])+$/u;

function isEmojiOnlyMessage(content: string): boolean {
  const trimmed = content.trim();
  if (!trimmed || !EMOJI_ONLY_REGEX.test(trimmed)) return false;
  const graphemes = typeof Intl !== 'undefined' && (Intl as any).Segmenter
    ? Array.from(new (Intl as any).Segmenter(undefined, { granularity: 'grapheme' }).segment(trimmed))
    : Array.from(trimmed);
  return graphemes.length > 0 && graphemes.length <= 3;
}

interface MessageBubbleProps {
  message: IMessage;
  isOwn: boolean;
  currentUserId: string;
  /** Called when the user picks "Edit" from the menu — opens the main ChatInput in edit mode. */
  onStartEdit?: (messageId: string, currentContent: string) => void;
  /** Highlights this bubble while it is the one being edited in the input bar. */
  isBeingEdited?: boolean;
  onDelete: (messageId: string, scope: 'self' | 'everyone') => Promise<void>;
  onSetReaction: (messageId: string, emoji: string) => Promise<void>;
  onRemoveReaction: (messageId: string) => Promise<void>;
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({ message, isOwn, currentUserId, onStartEdit, isBeingEdited, onDelete, onSetReaction, onRemoveReaction }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [reactionPickerOpen, setReactionPickerOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const reactionPickerRef = useRef<HTMLDivElement>(null);
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Close menu on outside click
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  // Close reaction picker on outside click
  useEffect(() => {
    if (!reactionPickerOpen) return;
    const handler = (e: MouseEvent) => {
      if (reactionPickerRef.current && !reactionPickerRef.current.contains(e.target as Node)) setReactionPickerOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [reactionPickerOpen]);

  const myReaction = message.reactions?.find(r => r.userId === currentUserId)?.emoji;

  const handlePickReaction = async (emoji: string) => {
    setReactionPickerOpen(false);
    try {
      if (myReaction === emoji) {
        await onRemoveReaction(message.id);
      } else {
        await onSetReaction(message.id, emoji);
      }
    } catch (e: any) {
      showError(e);
    }
  };

  // Group reactions by emoji for display
  const reactionGroups: { emoji: string; count: number; mine: boolean }[] = [];
  (message.reactions || []).forEach(r => {
    const group = reactionGroups.find(g => g.emoji === r.emoji);
    if (group) {
      group.count++;
      if (r.userId === currentUserId) group.mine = true;
    } else {
      reactionGroups.push({ emoji: r.emoji, count: 1, mine: r.userId === currentUserId });
    }
  });

  const formatTimestamp = (timestamp: number | string | Date) => {
    let date: Date;
    if (typeof timestamp === 'string' && !timestamp.includes('Z') && !timestamp.includes('+')) {
      date = new Date(timestamp.replace(' ', 'T')); // no 'Z' — MySQL DATETIME is server-local, not UTC
    } else {
      date = new Date(timestamp);
    }
    const now = new Date();
    if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' }) +
      ' ' + date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  const openMenu = () => setMenuOpen(true);

  // Long press for mobile
  const onTouchStart = () => {
    longPressTimer.current = setTimeout(openMenu, 500);
  };
  const onTouchEnd = () => {
    if (longPressTimer.current) { clearTimeout(longPressTimer.current); longPressTimer.current = null; }
  };

  const showError = (e: any) => {
    const msg = e?.response?.data?.message || e?.message || 'Something went wrong';
    setErrorMsg(msg);
    setTimeout(() => setErrorMsg(null), 4000);
  };

  const handleDelete = async (scope: 'self' | 'everyone') => {
    setDeleteConfirm(false);
    setMenuOpen(false);
    try {
      await onDelete(message.id, scope);
    } catch (e: any) {
      showError(e);
    }
  };

  const isEmojiOnly = !message.mediaUrl && !!message.content && isEmojiOnlyMessage(message.content);

  const bubbleStyle: React.CSSProperties = {
    padding: isEmojiOnly ? '0' : '10px 14px',
    borderRadius: '18px',
    backgroundColor: isEmojiOnly ? 'transparent' : (isOwn ? 'var(--chat-bubble-color, var(--primary-color))' : 'var(--chat-bubble-received-color, var(--background-paper))'),
    color: isOwn ? 'var(--chat-bubble-text, #ffffff)' : 'var(--chat-bubble-received-text, var(--text-primary))',
    border: isEmojiOnly ? 'none' : (isBeingEdited ? '2px solid var(--primary-color)' : (isOwn ? 'none' : '1px solid var(--border-color)')),
    boxShadow: isEmojiOnly ? 'none' : '0 1px 2px rgba(0,0,0,0.1)',
    opacity: isBeingEdited ? 0.85 : 1,
    wordWrap: 'break-word',
    overflowWrap: 'break-word',
    position: 'relative',
    cursor: 'default',
    // 'none' (not 'text') — text selection on Android triggers Chrome's
    // "Search with Google" / web-search popup on the selected words, even
    // when the selection isn't copyable. Disabling selection entirely avoids it.
    userSelect: 'none',
    WebkitUserSelect: 'none',
  };

  return (
    <div style={{ display: 'flex', justifyContent: isOwn ? 'flex-end' : 'flex-start', marginBottom: '8px', width: '100%' }}>
      <style>{`.message-bubble-wrapper:hover .message-react-btn { opacity: 1 !important; }`}</style>
      <div
        className="message-bubble-wrapper"
        style={{ position: 'relative', maxWidth: '70%' }}
        onContextMenu={e => { e.preventDefault(); openMenu(); }}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onTouchMove={onTouchEnd}
      >
        <div style={bubbleStyle}>
          {message.content && (
            <div style={isEmojiOnly
              ? { fontSize: '48px', lineHeight: 1.2, marginBottom: '0' }
              : { marginBottom: message.mediaUrl ? '8px' : '0' }
            }>{message.content}</div>
          )}
          {message.mediaUrl && (
            <MessageMedia
              mediaType={message.mediaType as 'image' | 'video' | 'link' | 'audio'}
              mediaUrl={message.mediaUrl}
              thumbnailUrl={message.thumbnailUrl}
              mediaKey={message.mediaKey}
              mediaMimeType={message.mediaMimeType}
              allowDownload={false}
            />
          )}

          {/* Inline error */}
          {errorMsg && (
            <div style={{ fontSize: 11, marginTop: 4, padding: '3px 6px', borderRadius: 4, background: 'rgba(244,67,54,0.15)', color: '#ff6b6b', wordBreak: 'break-word' }}>
              {errorMsg}
            </div>
          )}

          {/* Timestamp row */}
          <div style={{ fontSize: 11, marginTop: 4, opacity: 0.7, textAlign: 'right', color: 'var(--info-color,#2196f3)' }}>
            {message.editedAt && <span style={{ marginRight: 4, fontStyle: 'italic' }}>edited</span>}
            {formatTimestamp(message.timestamp)}
            {isOwn && (
              <span style={{ marginLeft: 4, verticalAlign: 'middle' }}>
                <span className="material-icons" style={{ fontSize: 14, verticalAlign: 'middle', color: 'var(--info-color,#2196f3)' }}>
                  {message.status === 'read' ? 'done_all' : 'done'}
                </span>
              </span>
            )}
          </div>
        </div>

        {/* Reaction pills */}
        {reactionGroups.length > 0 && (
          <div style={{
            display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap',
            justifyContent: isOwn ? 'flex-end' : 'flex-start'
          }}>
            {reactionGroups.map(g => (
              <button
                key={g.emoji}
                onClick={() => handlePickReaction(g.emoji)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 3,
                  fontSize: 12, padding: '2px 6px', borderRadius: 12,
                  border: g.mine ? '1px solid var(--primary-color)' : '1px solid var(--border-color)',
                  background: g.mine ? 'rgba(var(--primary-color-rgb, 33,150,243), 0.12)' : 'var(--background-paper)',
                  color: 'var(--text-primary)', cursor: 'pointer'
                }}
              >
                <span>{g.emoji}</span>
                {g.count > 1 && <span>{g.count}</span>}
              </button>
            ))}
          </div>
        )}

        {/* React button */}
        <button
          className="message-react-btn"
          onClick={() => setReactionPickerOpen(o => !o)}
          style={{
            position: 'absolute', top: -10,
            [isOwn ? 'left' : 'right']: -10,
            width: 24, height: 24, borderRadius: '50%',
            border: '1px solid var(--border-color)',
            background: 'var(--background-paper)',
            color: 'var(--text-secondary)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 13, opacity: 0, transition: 'opacity 0.15s ease'
          }}
          title="React"
        >
          😊
        </button>

        {/* Reaction picker popup */}
        {reactionPickerOpen && (
          <div
            ref={reactionPickerRef}
            style={{
              position: 'absolute', zIndex: 1000,
              [isOwn ? 'right' : 'left']: 0,
              top: '100%', marginTop: 4,
              background: 'var(--background-paper)',
              border: '1px solid var(--border-color)',
              borderRadius: 20, padding: '6px 8px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              display: 'flex', gap: 6
            }}
          >
            {QUICK_REACTIONS.map(emoji => (
              <button
                key={emoji}
                onClick={() => handlePickReaction(emoji)}
                style={{
                  border: 'none', background: myReaction === emoji ? 'var(--border-color)' : 'transparent',
                  borderRadius: 12, fontSize: 18, cursor: 'pointer', padding: '2px 4px'
                }}
              >
                {emoji}
              </button>
            ))}
          </div>
        )}

        {/* Context menu */}
        {menuOpen && (
          <div
            ref={menuRef}
            style={{
              position: 'absolute', zIndex: 1000,
              [isOwn ? 'right' : 'left']: 0,
              bottom: '100%', marginBottom: 4,
              background: 'var(--background-paper)',
              border: '1px solid var(--border-color)',
              borderRadius: 10, overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
              minWidth: 170,
            }}
          >
            {onStartEdit && !message.mediaUrl && (
              <button
                onClick={() => { onStartEdit(message.id, message.content); setMenuOpen(false); }}
                style={menuItemStyle}
              >
                <span className="material-icons" style={{ fontSize: 16, marginRight: 8 }}>edit</span>
                Edit
              </button>
            )}
            <button
              onClick={() => { handleDelete('self'); }}
              style={menuItemStyle}
            >
              <span className="material-icons" style={{ fontSize: 16, marginRight: 8 }}>visibility_off</span>
              Delete for me
            </button>
            {isOwn && (
              <button
                onClick={() => { setMenuOpen(false); setDeleteConfirm(true); }}
                style={{ ...menuItemStyle, color: 'var(--error-color,#f44336)' }}
              >
                <span className="material-icons" style={{ fontSize: 16, marginRight: 8 }}>delete_forever</span>
                Delete for everyone
              </button>
            )}
          </div>
        )}

        {/* Delete for everyone confirm */}
        {deleteConfirm && (
          <div style={{
            position: 'absolute', zIndex: 1001,
            [isOwn ? 'right' : 'left']: 0,
            bottom: '100%', marginBottom: 4,
            background: 'var(--background-paper)',
            border: '1px solid var(--error-color,#f44336)',
            borderRadius: 10, padding: '12px 16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            minWidth: 200,
          }}>
            <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--text-primary)' }}>
              Delete this message for everyone?<br />
              <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>This cannot be undone.</span>
            </p>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => handleDelete('everyone')} style={{ flex: 1, padding: '6px', borderRadius: 6, border: 'none', background: 'var(--error-color,#f44336)', color: 'white', cursor: 'pointer', fontSize: 12 }}>Delete</button>
              <button onClick={() => setDeleteConfirm(false)} style={{ flex: 1, padding: '6px', borderRadius: 6, border: '1px solid var(--border-color)', background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer', fontSize: 12 }}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const menuItemStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', width: '100%',
  padding: '10px 14px', border: 'none', background: 'transparent',
  color: 'var(--text-primary)', cursor: 'pointer', fontSize: 14,
  textAlign: 'left',
};
